import { faEllipsisVertical, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback } from "react";

import { AlertDialog, Button, DropdownMenu } from "@skydio/rivet-ui";

import { useDeleteIdentityProvider } from "hooks/mutation/deleteIdentityProvider";
import { displayMutationResults } from "utils/mutations";

import UpdateSsoModal from "./UpdateSsoModal";

import type { identityprovidertype } from "__generated__/OrgLoginMethods_loginMethods.graphql";

interface idp {
  id: string;
  uuid: string;
  name: string;
  providerType: identityprovidertype;
  oidcIssuer: string | null;
  oidcUsePkce: string | null;
  oidcClientId: string | null;
  axonConnectDomain: string | null;
}

export interface SsoMenuDropdownProps {
  identityProvider: idp;
  orgNodeId: string;
  overrideCount: number;
  enabled: boolean;
  connectionId?: string;
}

const SsoMenuDropdown = ({
  identityProvider,
  orgNodeId,
  overrideCount,
  enabled,
  connectionId,
}: SsoMenuDropdownProps) => {
  const [deleteAlertVisible, setDeleteAlertVisible] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [deleteIdentityProvider] = useDeleteIdentityProvider(
    displayMutationResults(response => response.deleteIdentityProviderConfiguration, {
      successMessage: () => "Identity provider deleted successfully",
    })
  );
  const handleDropdownItemSelect = useCallback(
    (e: Event) => {
      e.preventDefault();
      setEditOpen(true);
    },
    [setEditOpen]
  );
  const handleAlertDialogDeleteSelect = useCallback((e: Event) => {
    e.preventDefault();
  }, []);

  const handleAlertDialogDeleteAction = useCallback(() => {
    deleteIdentityProvider(identityProvider.id, [connectionId ?? ""]);
    setDeleteAlertVisible(false);
  }, [deleteIdentityProvider, identityProvider.id, connectionId]);

  return (
    <>
      <DropdownMenu modal={false}>
        <DropdownMenu.Trigger asChild>
          <Button isIconOnly={true} variant="light" className="bg-transparent">
            <FontAwesomeIcon icon={faEllipsisVertical} />
          </Button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content>
          <DropdownMenu.Item onSelect={handleDropdownItemSelect}>Edit</DropdownMenu.Item>
          {/* Delete button */}
          <AlertDialog open={deleteAlertVisible} onOpenChange={setDeleteAlertVisible}>
            <AlertDialog.Trigger asChild>
              <DropdownMenu.Item
                className="text-red focus:bg-red-50"
                onSelect={handleAlertDialogDeleteSelect}
                disabled={overrideCount > 0 || enabled}
              >
                <FontAwesomeIcon icon={faTrash} />
                Delete
              </DropdownMenu.Item>
            </AlertDialog.Trigger>
            <AlertDialog.Content>
              <AlertDialog.Title>Delete Identity Provider</AlertDialog.Title>
              <AlertDialog.Description>This action cannot be undone.</AlertDialog.Description>
              <AlertDialog.Footer>
                <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                <AlertDialog.Action onClick={handleAlertDialogDeleteAction} destructive asChild>
                  <Button variant="primary"> Delete</Button>
                </AlertDialog.Action>
              </AlertDialog.Footer>
            </AlertDialog.Content>
          </AlertDialog>
        </DropdownMenu.Content>
      </DropdownMenu>

      <UpdateSsoModal
        idp={identityProvider}
        orgNodeId={orgNodeId}
        isOpen={editOpen}
        setIsOpen={setEditOpen}
        connection={connectionId}
      />
    </>
  );
};

export default SsoMenuDropdown;
